import React, { useState, useEffect, useRef } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import InvoiceTemplate from '../../common/components/InvoiceTemplate';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Title,
  Group,
  Button,
  Paper,
  Select,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  TextInput,
  NumberInput,
  Text,
  ScrollArea,
  Image,
  Stack,
  Box,
  Tabs,
  Card,
  Alert,
  List,
  ThemeIcon,
  Divider,
  Radio,
  Checkbox,
  LoadingOverlay,
  Loader,
  Grid,
  Pagination,
  Textarea
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { notifications } from '@mantine/notifications';
import { 
  IconPlus, IconCash, IconFilter, IconPrinter, IconEye, IconHistory, IconX, IconSettings,
  IconReceipt, IconInfoCircle, IconTrash, IconMapPin, IconCheck, IconCalendar, IconClock,
  IconRefresh, IconSearch, IconPhoto, IconArrowRight
} from '@tabler/icons-react';

const Billing = () => {
  const [invoices, setInvoices] = useState([]);
  const [residents, setResidents] = useState([]);
  const [settings, setSettings] = useState({}); 
  
  // FILTERS STATE
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDateRange, setFilterDateRange] = useState([null, null]); 
  
  const [venueList, setVenueList] = useState([]); 
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenuePrice, setNewVenuePrice] = useState('');

  const [activeTab, setActiveTab] = useState('invoices');
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false); 
  const [printing, setPrinting] = useState(false); 
  
  // Pagination State
  const [activePage, setActivePage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  const [imagePreview, setImagePreview] = useState(null); 
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [verificationData, setVerificationData] = useState(null); 
  const [historyData, setHistoryData] = useState([]);

  // Admin Payment Form State
  const [paymentForm, setPaymentForm] = useState({ 
      amountPaid: '', 
      paymentMethod: 'cash', 
      referenceNumber: '' 
  });
  
  const [verifyAmount, setVerifyAmount] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  const [currentUser, setCurrentUser] = useState(null);

  const componentRef = useRef(null);

  // --- HELPER: GENERATE TIME SLOTS (8 AM - 11 PM) ---
  const TIME_SLOTS = [
    "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
    "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", 
    "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM", 
    "10:00 PM", "11:00 PM"
  ];

  // Helper to get valid END times based on START time
  const getEndTimeOptions = (startTime) => {
    if (!startTime) return [];
    const startIndex = TIME_SLOTS.indexOf(startTime);
    if (startIndex === -1) return [];
    return TIME_SLOTS.slice(startIndex + 1);
  };

  // --- HELPER: CALCULATE HOURS FOR PRICING ---
  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [time, modifier] = timeStr.split(' ');
    let [hours] = time.split(':');
    hours = parseInt(hours, 10);
    if (hours === 12) {
        hours = modifier === 'PM' ? 12 : 0;
    } else {
        hours = modifier === 'PM' ? hours + 12 : hours;
    }
    return hours;
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    const startHour = parseTime(start);
    const endHour = parseTime(end);
    return Math.max(0, endHour - startHour);
  };

  // --- PRINTING LOGIC ---
  const handlePrintReceipt = async () => {
    const input = componentRef.current;
    if (!input) {
        notifications.show({ title: 'Error', message: 'Receipt content not found.', color: 'red' });
        return;
    }

    setPrinting(true);
    try {
        const canvas = await html2canvas(input, {
            scale: 2, 
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth(); 
        const pdfHeight = pdf.internal.pageSize.getHeight(); 

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        
        const ratio = pdfWidth / imgWidth;
        const scaledHeight = imgHeight * ratio;

        const imgData = canvas.toDataURL('image/png');

        if (scaledHeight > pdfHeight) {
             pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, scaledHeight);
        } else {
             pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, scaledHeight); 
        }

        pdf.autoPrint();
        const blob = pdf.output('bloburl');
        window.open(blob, '_blank');

    } catch (error) {
        console.error("Print Error:", error);
        notifications.show({ title: 'Error', message: 'Failed to generate receipt', color: 'red' });
    }
    setPrinting(false);
  };

  // Invoice Generation State Logic
  const [payerType, setPayerType] = useState('resident');
  const [selectedResident, setSelectedResident] = useState('');
  const [manualName, setManualName] = useState('');
  const [isDuesSelected, setIsDuesSelected] = useState(false);
  const [isStickerSelected, setIsStickerSelected] = useState(false);
  const [selectedVenues, setSelectedVenues] = useState([]); 
  const [duesRange, setDuesRange] = useState({ startMonth: '', endMonth: '', year: new Date().getFullYear().toString() });
  const [stickerCount, setStickerCount] = useState(1);
  const [venueSchedules, setVenueSchedules] = useState({});
  const [cashierAmountPaid, setCashierAmountPaid] = useState('');
  
  const [cashierPaymentMethod, setCashierPaymentMethod] = useState('cash'); 
  
  const [settingsForm, setSettingsForm] = useState({ monthly_dues: 0, dueDay: 15, discountAmount: 0, discountDay: 0, sticker_fee: 0 });

  useEffect(() => {
    fetchInvoices();
    fetchResidents();
    fetchSettings();
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setActivePage(1);
  }, [filterStatus, filterName, filterType, filterDateRange]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/billing/invoices');
      setInvoices(data);
    } catch (error) { console.error(error); }
    setLoading(false);
  };

  const fetchResidents = async () => {
    try {
      const { data } = await axios.get('/api/residents');
      setResidents(data);
    } catch (error) { console.error(error); }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get('/api/billing/settings');
      const settingsObj = {};
      data.forEach(s => {
        if (s.type === 'venue_list') {
            const list = s.value || [];
            const normalizedList = list.map(v => (typeof v === 'string' ? { name: v, price: 0 } : v));
            setVenueList(normalizedList);
        } else {
            settingsObj[s.type] = s.amount;
            if (s.type === 'monthly_dues') {
                settingsObj.dueDay = s.dueDay;
                settingsObj.discountAmount = s.discountAmount || 0;
                settingsObj.discountDay = s.discountDay || 0;
            }
        }
      });
      setSettings(settingsObj);
      setSettingsForm(prev => ({ ...prev, ...settingsObj }));
    } catch (error) { console.error("Error fetching settings:", error); }
  };

  const handleSaveSettings = async (type) => {
    try {
        const payload = {
            type,
            amount: settingsForm[type],
            dueDay: type === 'monthly_dues' ? settingsForm.dueDay : undefined,
            discountAmount: type === 'monthly_dues' ? settingsForm.discountAmount : undefined,
            discountDay: type === 'monthly_dues' ? settingsForm.discountDay : undefined,
        };
        await axios.put('/api/billing/settings', payload);
        notifications.show({ title: 'Success', message: 'Settings updated', color: 'green' });
        fetchSettings();
    } catch (error) {
        notifications.show({ title: 'Error', message: 'Failed to update setting', color: 'red' });
    }
  };

  const handleGenerateMonthly = async () => {
      setGenerating(true);
      try {
          const { data } = await axios.post('/api/billing/generate-monthly');
          notifications.show({ title: 'Success', message: data.message, color: 'green' });
          fetchInvoices(); 
      } catch (error) {
          notifications.show({ title: 'Error', message: error.response?.data?.message || 'Failed to generate billings', color: 'red' });
      }
      setGenerating(false);
  };

  const handleAddVenue = async () => {
    if (!newVenueName.trim() || !newVenuePrice) return notifications.show({ title: 'Error', message: 'Name and Price required', color: 'yellow' });
    const newVenueObj = { name: newVenueName, price: Number(newVenuePrice) };
    const updatedList = [...venueList, newVenueObj];
    setVenueList(updatedList);
    setNewVenueName(''); setNewVenuePrice('');
    try { await axios.put('/api/billing/settings', { type: 'venue_list', value: updatedList }); notifications.show({ title: 'Success', message: 'Venue added', color: 'green' }); } catch (error) {}
  };

  const handleDeleteVenue = async (venueName) => {
    const updatedList = venueList.filter(v => v.name !== venueName);
    setVenueList(updatedList);
    try { await axios.put('/api/billing/settings', { type: 'venue_list', value: updatedList }); } catch (error) {}
  };

  // --- UPDATED FILTER LOGIC ---
  const filteredInvoices = invoices.filter(invoice => {
    const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;
    const matchesType = filterType === 'all' || invoice.type === filterType;
    const nameToSearch = invoice.resident 
        ? `${invoice.resident.firstName} ${invoice.resident.lastName}`.toLowerCase() 
        : (invoice.manualName || '').toLowerCase();
    const matchesName = nameToSearch.includes(filterName.toLowerCase());

    let matchesDate = true;
    if (filterDateRange[0] && filterDateRange[1]) {
        const invoiceDate = new Date(invoice.createdAt).setHours(0, 0, 0, 0);
        const startDate = new Date(filterDateRange[0]).setHours(0, 0, 0, 0);
        const endDate = new Date(filterDateRange[1]).setHours(0, 0, 0, 0);
        matchesDate = invoiceDate >= startDate && invoiceDate <= endDate;
    }

    return matchesStatus && matchesType && matchesName && matchesDate;
  });

  // --- PAGINATION LOGIC ---
  const paginatedInvoices = filteredInvoices.slice(
    (activePage - 1) * ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE
  );

  const handleNameChange = (e) => {
      const val = e.target.value;
      if (val === '' || /^[A-Za-z\s]+$/.test(val)) {
          const formatted = val.replace(/\b\w/g, c => c.toUpperCase());
          setManualName(formatted);
      }
  };

  const getAutoDiscountPerMonth = () => {
      if (!settings.discountAmount || settings.discountAmount <= 0) return 0;
      if (payerType !== 'resident' || !selectedResident) return 0;
      const residentObj = residents.find(r => r._id === selectedResident);
      const isGoodStanding = residentObj?.status?.toLowerCase() === 'good_standing';
      if (!isGoodStanding) return 0;
      const currentDay = new Date().getDate();
      if (currentDay > (settings.discountDay || 0)) return 0;
      return settings.discountAmount;
  };

  const calculateTotal = () => {
      let total = 0;
      if (isDuesSelected && duesRange.startMonth && duesRange.endMonth) {
          const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
          const startIdx = months.indexOf(duesRange.startMonth);
          const endIdx = months.indexOf(duesRange.endMonth);
          if (startIdx !== -1 && endIdx !== -1 && endIdx >= startIdx) {
              const count = (endIdx - startIdx) + 1;
              let duesAmount = (settings.monthly_dues || 0) * count;
              const discountPerMonth = getAutoDiscountPerMonth();
              duesAmount -= (discountPerMonth * count);
              total += duesAmount;
          }
      }
      if (isStickerSelected) total += (settings.sticker_fee || 0) * stickerCount;
      
      // UPDATED: Calculate Venue Total using Hours
      selectedVenues.forEach(vName => {
          const v = venueList.find(i => i.name === vName);
          const schedule = venueSchedules[vName];
          if(v && schedule?.startTime && schedule?.endTime) {
              const hours = calculateHours(schedule.startTime, schedule.endTime);
              total += (v.price * hours);
          }
      });
      return total;
  };

  useEffect(() => {
      if (openInvoiceDialog) {
          setCashierAmountPaid(calculateTotal());
          setCashierPaymentMethod('cash'); // Reset default
      }
  }, [isDuesSelected, isStickerSelected, selectedVenues, duesRange, stickerCount, selectedResident, openInvoiceDialog, venueSchedules]);

  const handleVenueCheck = (checked, venueName) => {
      if (checked) {
          setSelectedVenues([...selectedVenues, venueName]);
          setVenueSchedules(prev => ({ 
              ...prev, 
              [venueName]: { date: null, startTime: '', endTime: '' } 
          }));
      } else {
          setSelectedVenues(selectedVenues.filter(v => v !== venueName));
      }
  };

  const updateVenueSchedule = (venueName, field, value) => {
      setVenueSchedules(prev => {
          const updated = { ...prev[venueName], [field]: value };
          if (field === 'startTime') {
              updated.endTime = ''; 
          }
          return { ...prev, [venueName]: updated };
      });
  };

  const handleSubmitInvoice = async () => {
    // 1. Validate Payer
    if (payerType === 'resident' && !selectedResident) return notifications.show({ title: 'Error', message: 'Please select a Resident', color: 'red' });
    if (payerType === 'non_resident' && !manualName) return notifications.show({ title: 'Error', message: 'Please enter Guest Name', color: 'red' });
    
    // 2. Validate at least one item selected
    if (!isDuesSelected && !isStickerSelected && selectedVenues.length === 0) {
        return notifications.show({ title: 'Error', message: 'Select at least one payment item', color: 'red' });
    }

    const items = [];

    // 3. Validate Monthly Dues
    if (isDuesSelected) {
        if(!duesRange.startMonth) return notifications.show({ title: 'Error', message: 'Monthly Dues: Start Month is required', color: 'red' });
        if(!duesRange.endMonth) return notifications.show({ title: 'Error', message: 'Monthly Dues: End Month is required', color: 'red' });
        
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const startIdx = months.indexOf(duesRange.startMonth);
        const endIdx = months.indexOf(duesRange.endMonth);
        
        if (endIdx < startIdx) return notifications.show({ title: 'Error', message: 'Monthly Dues: Invalid Date Range', color: 'red' });
        
        const count = (endIdx - startIdx) + 1;
        const amount = (settings.monthly_dues || 0) * count;
        const desc = `Monthly Dues: ${duesRange.startMonth} - ${duesRange.endMonth} ${duesRange.year}`;
        const discountVal = getAutoDiscountPerMonth() * count;

        items.push({ type: 'monthly_dues', amount, discountApplied: discountVal, description: desc });
    }

    // 4. Validate Sticker
    if (isStickerSelected) {
        if(stickerCount < 1) return notifications.show({ title: 'Error', message: 'Sticker quantity must be at least 1', color: 'red' });
        const amount = (settings.sticker_fee || 0) * stickerCount;
        items.push({ type: 'sticker_fee', amount, description: `Vehicle Sticker (${stickerCount})` });
    }

    // 5. Validate Venue Rentals (Date, Start Time, End Time)
    for (const vName of selectedVenues) {
        const v = venueList.find(i => i.name === vName);
        const schedule = venueSchedules[vName];
        
        if (!schedule?.date) return notifications.show({ title: 'Error', message: `Venue ${vName}: Date is required`, color: 'red' });
        if (!schedule?.startTime) return notifications.show({ title: 'Error', message: `Venue ${vName}: Start Time is required`, color: 'red' });
        if (!schedule?.endTime) return notifications.show({ title: 'Error', message: `Venue ${vName}: End Time is required`, color: 'red' });

        // UPDATED: Calculate Amount based on hours
        const hours = calculateHours(schedule.startTime, schedule.endTime);
        const totalAmount = v.price * hours;

        items.push({ 
            type: 'venue_rental', 
            amount: totalAmount, 
            description: `Venue Rental: ${vName} on ${new Date(schedule.date).toLocaleDateString()} (${schedule.startTime} - ${schedule.endTime}) [${hours} Hours]`,
            venueName: vName,
            rentalDate: new Date(schedule.date), 
            rentalTime: `${schedule.startTime} - ${schedule.endTime}`
        });
    }

    // 6. Validate Payment Amount
    if (cashierAmountPaid === '' || cashierAmountPaid < 0) {
        return notifications.show({ title: 'Error', message: 'Amount Received is required', color: 'red' });
    }

    let mainDescription = '';
    if (items.length > 0) {
        mainDescription = items.map(item => item.description).join(', ');
    }

    try {
      const payload = {
          residentId: payerType === 'resident' ? selectedResident : null,
          manualName: payerType === 'non_resident' ? manualName : null,
          items,
          description: mainDescription,
          amountPaid: cashierAmountPaid,
          paymentMethod: cashierPaymentMethod, 
      };

      await axios.post('/api/billing/invoices', payload);
      
      setOpenInvoiceDialog(false);
      notifications.show({ title: 'Success', message: 'Receipt Generated!', color: 'green' });
      
      // Reset Form
      setIsDuesSelected(false); setIsStickerSelected(false); setSelectedVenues([]);
      setDuesRange({ startMonth: '', endMonth: '', year: new Date().getFullYear().toString() });
      setStickerCount(1);
      setVenueSchedules({});
      setCashierAmountPaid('');
      
      fetchInvoices();
    } catch (error) {
      notifications.show({ title: 'Error', message: error.response?.data?.message || 'Error', color: 'red' });
    }
  };

  const openPayModal = async (invoice) => {
    setSelectedInvoice(invoice); 
    const remainingBalance = invoice.totalAmount - (invoice.amountPaid || 0);
    
    // Default form state
    setPaymentForm({ 
        amountPaid: remainingBalance, 
        paymentMethod: 'cash', 
        referenceNumber: '' 
    });
    setRejectionReason('');
    
    if (invoice.status === 'pending_approval') {
        try {
            const { data } = await axios.get(`/api/billing/transaction/${invoice._id}`);
            setVerificationData(data);
            setVerifyAmount(data.amountPaid);
            // Pre-fill form with resident's submission
            setPaymentForm({
                amountPaid: data.amountPaid,
                paymentMethod: data.paymentMethod,
                referenceNumber: data.referenceNumber
            });
        } catch (error) { console.error(error); }
    } else {
        setVerificationData(null);
    }
    setOpenPaymentDialog(true);
  };

  const handleRefInput = (val) => {
      const rawValue = val.replace(/\D/g, '');
      const limitedValue = rawValue.slice(0, 13);
      let formattedValue = limitedValue;
      if (limitedValue.length > 4) {
        formattedValue = `${limitedValue.slice(0, 4)} ${limitedValue.slice(4)}`;
      }
      if (limitedValue.length > 7) {
        formattedValue = `${limitedValue.slice(0, 4)} ${limitedValue.slice(4, 7)} ${limitedValue.slice(7)}`;
      }
      setPaymentForm({ ...paymentForm, referenceNumber: formattedValue });
  };

  // --- ADMIN ACTION (PAY/APPROVE) ---
  const handleAdminAction = async (actionType) => {
    try {
      if (actionType !== 'rejected') {
          const amount = parseFloat(paymentForm.amountPaid || verifyAmount);
          if (!amount || amount <= 0) {
              return notifications.show({ title: 'Validation Error', message: 'Amount Paid is required.', color: 'red' });
          }
          if (amount > 100000) {
              return notifications.show({ title: 'Validation Error', message: 'Amount cannot exceed ₱100,000.', color: 'red' });
          }

          if (!paymentForm.paymentMethod) {
              return notifications.show({ title: 'Validation Error', message: 'Payment Method is required.', color: 'red' });
          }

          if (paymentForm.paymentMethod !== 'cash') {
              if (!paymentForm.referenceNumber) {
                  return notifications.show({ title: 'Validation Error', message: 'Reference Number is required for non-cash payments.', color: 'red' });
              }
              if (paymentForm.paymentMethod === 'gcash') {
                  const refNo = paymentForm.referenceNumber.replace(/\s/g, '');
                  if (refNo.length !== 13) {
                      return notifications.show({ title: 'Validation Error', message: 'GCash Reference No. must be exactly 13 digits.', color: 'red' });
                  }
              }
          }
      }

      if (selectedInvoice && selectedInvoice.status === 'pending_approval') {
        if (!verificationData) return;
        
        if (actionType === 'rejected' && !rejectionReason) {
            return notifications.show({ title: 'Warning', message: 'Rejection reason is required.', color: 'yellow' });
        }

        await axios.put(`/api/billing/pay/approve/${verificationData._id}`, { 
            action: actionType === 'rejected' ? 'reject' : 'approve', 
            rejectionReason, 
            confirmedAmount: parseFloat(verifyAmount)
        });
        
        notifications.show({ 
            title: 'Success', 
            message: actionType === 'rejected' ? 'Payment Rejected' : 'Payment Approved & Verified', 
            color: 'green' 
        });

      } else {
        await axios.post('/api/billing/pay', { 
            invoiceIds: [selectedInvoice._id], 
            amountPaid: parseFloat(paymentForm.amountPaid),
            paymentMethod: paymentForm.paymentMethod,
            referenceNumber: paymentForm.referenceNumber
        });
        notifications.show({ title: 'Success', message: 'Manual Payment Recorded', color: 'green' });
      }

      setOpenPaymentDialog(false);
      fetchInvoices(); 
    } catch (error) { 
        console.error(error);
        notifications.show({ title: 'Error', message: error.response?.data?.message || 'Action failed', color: 'red' }); 
    }
  };

  const openHistoryModal = async (invoice) => {
    setSelectedInvoice(invoice);
    try { 
      const { data } = await axios.get(`/api/billing/history/${invoice._id}`); 
      setHistoryData(data); 
      setOpenHistoryDialog(true); 
    } catch (error) { console.error(error); }
  };

  const openPrintModal = (invoice) => {
    setSelectedInvoice({ 
        ...invoice, 
        totalAmount: invoice.totalAmount || invoice.amount || 0,
        receivedBy: currentUser?.name || 'Admin' 
    });
    setOpenPrintDialog(true);
  };

  const getInvoiceTypeLabel = (invoice) => {
      if (invoice.type === 'consolidated') {
          if (invoice.items && invoice.items.length > 0) {
              const types = [...new Set(invoice.items.map(i => i.type.replace('_', ' ')))];
              return types.join(', '); 
          }
          return 'Consolidated';
      }
      return invoice.type.replace('_', ' ');
  };

  const formatMoney = (amount) => Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Billing & Invoices</Title>
      </Group>

      {/* --- HIDDEN PRINT AREA --- */}
      <div style={{ height: '0px', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, left: '-9999px', width: '210mm' }}>
            <div ref={componentRef} style={{ backgroundColor: 'white', padding: '40px' }}>
                {selectedInvoice && <InvoiceTemplate invoice={selectedInvoice} />}
            </div>
        </div>
      </div>

      <Tabs value={activeTab} onChange={setActiveTab} color="teal">
        <Tabs.List mb="md">
          <Tabs.Tab value="invoices" leftSection={<IconReceipt size={18} />}>Invoices</Tabs.Tab>
          <Tabs.Tab value="settings" leftSection={<IconSettings size={18} />}>Price Settings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="invoices">
           <Paper p="md" mb="lg" withBorder>
             <Group justify="space-between" align="center">
                <Group gap="sm" style={{flex: 1}}>
                    <Group gap="xs">
                        <IconFilter size={20} color="gray" />
                        <Text size="sm" fw={500}>Filters:</Text>
                    </Group>
                    <TextInput 
                        placeholder="Search Name..." 
                        leftSection={<IconSearch size={16}/>}
                        value={filterName}
                        onChange={(e) => setFilterName(e.currentTarget.value)}
                        style={{ width: 180 }}
                    />
                    <DatePickerInput
                        type="range"
                        placeholder="Select Date Range"
                        value={filterDateRange}
                        onChange={setFilterDateRange}
                        clearable
                        style={{ width: 200 }}
                    />
                    <Select 
                        placeholder="Filter Type"
                        value={filterType} 
                        onChange={setFilterType} 
                        data={[
                            { value: 'all', label: 'All Types' }, 
                            { value: 'monthly_dues', label: 'Monthly Dues' },
                            { value: 'sticker_fee', label: 'Vehicle Sticker' },
                            { value: 'venue_rental', label: 'Venue Rental' },
                        ]}
                        allowDeselect={false} 
                        style={{ width: 150 }}
                    />
                    <Select 
                        value={filterStatus} 
                        onChange={setFilterStatus} 
                        data={[
                            { value: 'all', label: 'All Status' }, 
                            { value: 'pending_approval', label: 'Pending Approval' }, 
                            { value: 'paid', label: 'Paid' }, 
                            { value: 'partial', label: 'Partial' }
                        ]} 
                        allowDeselect={false} 
                        style={{ width: 150 }} 
                    />
                </Group>
                <Button leftSection={<IconPlus size={18} />} onClick={() => setOpenInvoiceDialog(true)}>Generate Invoice</Button>
             </Group>
           </Paper>

           <Paper withBorder pos="relative">
            <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />
            <ScrollArea>
            <Table striped highlightOnHover style={{ minWidth: '900px' }}>
                <Table.Thead>
                <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Payer</Table.Th>
                    <Table.Th>Type</Table.Th> 
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Amount Due</Table.Th>
                    <Table.Th>Paid</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Action</Table.Th>
                </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                {paginatedInvoices.map((invoice) => (
                    <Table.Tr key={invoice._id} bg={invoice.status === 'pending_approval' ? 'orange.1' : undefined}>
                    <Table.Td>{new Date(invoice.createdAt).toLocaleDateString()}</Table.Td>
                    <Table.Td>{invoice.resident ? `${invoice.resident.lastName}, ${invoice.resident.firstName}` : `${invoice.manualName} (Guest)`}</Table.Td>
                    <Table.Td style={{ textTransform: 'capitalize' }}>
                        <Badge variant="outline" color="gray" size="sm">{getInvoiceTypeLabel(invoice)}</Badge>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: '250px' }}>
                        <Text truncate title={invoice.description}>{invoice.description}</Text>
                    </Table.Td>
                    <Table.Td fw="bold">₱{formatMoney(invoice.totalAmount)}</Table.Td>
                    <Table.Td>₱{formatMoney(invoice.amountPaid || 0)}</Table.Td>
                    <Table.Td><Badge color={invoice.status === 'paid' ? 'green' : invoice.status === 'pending_approval' ? 'orange' : 'red'}>{invoice.status === 'pending_approval' ? 'NEEDS REVIEW' : invoice.status.toUpperCase()}</Badge></Table.Td>
                    <Table.Td>
                        <Group gap="xs">
                        <Tooltip label="View">
                            <ActionIcon color="teal" variant="light" onClick={() => openPrintModal(invoice)}><IconEye size={18} /></ActionIcon>
                        </Tooltip>
                        <Tooltip label="History">
                            <ActionIcon variant="default" onClick={() => openHistoryModal(invoice)}><IconHistory size={18}/></ActionIcon>
                        </Tooltip>
                        <Tooltip label="Print">
                            <ActionIcon color="blue" variant="light" onClick={() => openPrintModal(invoice)}><IconPrinter size={18}/></ActionIcon>
                        </Tooltip>
                        {invoice.status !== 'paid' && (
                            <Tooltip label={invoice.status === 'pending_approval' ? "Review" : "Pay"}>
                                <ActionIcon color={invoice.status === 'pending_approval' ? "orange" : "green"} variant="light" onClick={() => openPayModal(invoice)}>
                                    {invoice.status === 'pending_approval' ? <IconEye size={18} /> : <IconCash size={18} />}
                                </ActionIcon>
                            </Tooltip>
                        )}
                        </Group>
                    </Table.Td>
                    </Table.Tr>
                ))}
                </Table.Tbody>
            </Table>
            </ScrollArea>
            
            <Group justify="flex-end" p="md" style={{ borderTop: '1px solid #eee' }}>
                <Pagination 
                    total={Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE)} 
                    value={activePage} 
                    onChange={setActivePage} 
                    color="teal"
                />
            </Group>
           </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="settings">
            <Grid gutter="lg">
                <Grid.Col span={4}>
                    <Card withBorder shadow="sm" p="lg" h="100%">
                        <Card.Section withBorder inheritPadding py="xs"><Text fw={500}>Monthly Dues</Text></Card.Section>
                        <Stack mt="md">
                            <NumberInput label="Base Amount (PHP)" value={settingsForm.monthly_dues} onChange={(val) => setSettingsForm({...settingsForm, monthly_dues: val})} decimalScale={2} fixedDecimalScale />
                            <NumberInput label="Default Due Day" min={1} max={31} value={settingsForm.dueDay} onChange={(val) => setSettingsForm({...settingsForm, dueDay: val})} />
                            <Alert icon={<IconInfoCircle size={16} />} title="Early Payment Discount" color="blue">Automatically applied for Good Standing residents before the cut-off.</Alert>
                            <NumberInput label="Discount Amount (PHP)" value={settingsForm.discountAmount} onChange={(val) => setSettingsForm({...settingsForm, discountAmount: val})} decimalScale={2} fixedDecimalScale />
                            <NumberInput label="Discount Cut-off Day" min={1} max={31} value={settingsForm.discountDay} onChange={(val) => setSettingsForm({...settingsForm, discountDay: val})} />
                            <Button fullWidth mt="md" onClick={() => handleSaveSettings('monthly_dues')}>Update Settings</Button>
                            <Divider my="md" label="For Testing" labelPosition="center" />
                            <Button fullWidth color="orange" variant="light" leftSection={<IconRefresh size={18} />} loading={generating} onClick={handleGenerateMonthly}>Generate Monthly Billings</Button>
                        </Stack>
                    </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                    <Card withBorder shadow="sm" p="lg" h="100%">
                        <Card.Section withBorder inheritPadding py="xs"><Text fw={500}>Venue Management</Text></Card.Section>
                        <Stack mt="md">
                            <Group grow>
                                <TextInput placeholder="Venue Name" value={newVenueName} onChange={(e) => setNewVenueName(e.target.value)} />
                                <NumberInput placeholder="Price" value={newVenuePrice} onChange={(val) => setNewVenuePrice(val)} />
                            </Group>
                            <Button onClick={handleAddVenue}>Add Venue</Button>
                            <Divider label="Venues" />
                            <List spacing="xs" size="sm" center>
                                {venueList.map((venue, index) => (
                                    <List.Item key={index} icon={<ThemeIcon color="blue" size={20} radius="xl"><IconMapPin size={12} /></ThemeIcon>}>
                                        <Group justify="space-between">
                                            <Text>{venue.name} (₱{venue.price || 0})</Text>
                                            <ActionIcon color="red" variant="transparent" onClick={() => handleDeleteVenue(venue.name)}><IconTrash size={14}/></ActionIcon>
                                        </Group>
                                    </List.Item>
                                ))}
                            </List>
                        </Stack>
                    </Card>
                </Grid.Col>
                <Grid.Col span={4}>
                    <Card withBorder shadow="sm" p="lg" h="100%">
                        <Card.Section withBorder inheritPadding py="xs"><Text fw={500}>Sticker Fee</Text></Card.Section>
                        <Stack mt="md">
                            <NumberInput label="Amount (PHP)" value={settingsForm.sticker_fee} onChange={(val) => setSettingsForm({...settingsForm, sticker_fee: val})} decimalScale={2} fixedDecimalScale />
                            <Button fullWidth mt="xl" onClick={() => handleSaveSettings('sticker_fee')}>Update</Button>
                        </Stack>
                    </Card>
                </Grid.Col>
            </Grid>
        </Tabs.Panel>
      </Tabs>

      <Modal opened={openInvoiceDialog} onClose={() => setOpenInvoiceDialog(false)} title="Generate Official Receipt" size="lg">
        <Box>
            <Radio.Group label="Payer Type" value={payerType} onChange={setPayerType} mb="md" withAsterisk>
                <Group mt="xs"><Radio value="resident" label="Registered Resident" /><Radio value="non_resident" label="Non-Resident / Guest" /></Group>
            </Radio.Group>
            
            {payerType === 'resident' ? (
                <Select withAsterisk required label="Resident" placeholder="Select Resident" data={residents.map(r => ({ value: r._id, label: `${r.lastName}, ${r.firstName}` }))} searchable mb="md" value={selectedResident} onChange={setSelectedResident} />
            ) : (
                <TextInput withAsterisk required label="Guest Name" placeholder="Juan Dela Cruz" mb="md" value={manualName} onChange={handleNameChange} maxLength={50} />
            )}
            
            <Divider my="md" label="Select Payment Items" labelPosition="center" />
            <Stack gap="md" mb="xl">
                
                {/* 1. Monthly Dues - HIDDEN if guest */}
                {payerType === 'resident' && (
                    <Card withBorder p="sm" bg={isDuesSelected ? "blue.0" : "transparent"} style={{borderColor: isDuesSelected ? 'blue' : 'lightgray'}}>
                        <Group align="flex-start">
                            <Checkbox label="Monthly Dues" checked={isDuesSelected} onChange={(e) => setIsDuesSelected(e.currentTarget.checked)} style={{marginTop: 6}} />
                            {isDuesSelected && (
                                <Stack gap="xs" style={{flex: 1}}>
                                    <Group grow>
                                        <Select withAsterisk placeholder="Start Month" data={['January','February','March','April','May','June','July','August','September','October','November','December']} value={duesRange.startMonth} onChange={(v) => setDuesRange({...duesRange, startMonth: v})} />
                                        <Select withAsterisk placeholder="End Month" data={['January','February','March','April','May','June','July','August','September','October','November','December']} value={duesRange.endMonth} onChange={(v) => setDuesRange({...duesRange, endMonth: v})} />
                                        <TextInput placeholder="Year" value={duesRange.year} disabled />
                                    </Group>
                                </Stack>
                            )}
                        </Group>
                    </Card>
                )}

                <Card withBorder p="sm" bg={isStickerSelected ? "blue.0" : "transparent"} style={{borderColor: isStickerSelected ? 'blue' : 'lightgray'}}>
                    <Group align="flex-start">
                        <Checkbox label="Vehicle Sticker" checked={isStickerSelected} onChange={(e) => setIsStickerSelected(e.currentTarget.checked)} style={{marginTop: 6}} />
                        {isStickerSelected && (
                            <Group style={{flex: 1}}>
                                <NumberInput withAsterisk label="Quantity" min={1} value={stickerCount} onChange={setStickerCount} w={100} />
                                <Text size="sm" mt={24}>x ₱{settings.sticker_fee}</Text>
                            </Group>
                        )}
                    </Group>
                </Card>
                <Text size="sm" fw={500} mt="xs">Venue Rentals</Text>
                {venueList.map((venue) => {
                    const isChecked = selectedVenues.includes(venue.name);
                    return (
                        <Card key={venue.name} withBorder p="sm" bg={isChecked ? "orange.0" : "transparent"} style={{borderColor: isChecked ? 'orange' : 'lightgray'}}>
                            <Stack>
                                <Group justify="space-between">
                                    <Checkbox label={`${venue.name} (₱${venue.price || 0})`} checked={isChecked} onChange={(e) => handleVenueCheck(e.currentTarget.checked, venue.name)} />
                                </Group>
                                {isChecked && (
                                    <Group grow>
                                        {/* DATE SELECTION */}
                                        <DatePickerInput 
                                            withAsterisk
                                            placeholder="Select Date" 
                                            leftSection={<IconCalendar size={16}/>} 
                                            minDate={new Date()} 
                                            value={venueSchedules[venue.name]?.date} 
                                            onChange={(val) => updateVenueSchedule(venue.name, 'date', val)} 
                                        />
                                        
                                        {/* START TIME SELECTION - FILTERED TO 8AM-10PM */}
                                        <Select 
                                            withAsterisk
                                            placeholder="Start Time" 
                                            leftSection={<IconClock size={16}/>} 
                                            data={TIME_SLOTS.slice(0, TIME_SLOTS.length - 1)} // Can't start at 11PM
                                            value={venueSchedules[venue.name]?.startTime} 
                                            onChange={(val) => updateVenueSchedule(venue.name, 'startTime', val)}
                                            searchable={false}
                                        />

                                        {/* END TIME SELECTION - DYNAMICALLY FILTERED */}
                                        <Select 
                                            withAsterisk
                                            placeholder="End Time" 
                                            leftSection={<IconArrowRight size={16}/>} 
                                            data={getEndTimeOptions(venueSchedules[venue.name]?.startTime)}
                                            value={venueSchedules[venue.name]?.endTime} 
                                            onChange={(val) => updateVenueSchedule(venue.name, 'endTime', val)}
                                            disabled={!venueSchedules[venue.name]?.startTime}
                                            searchable={false}
                                        />
                                    </Group>
                                )}
                            </Stack>
                        </Card>
                    );
                })}
            </Stack>
            <Group justify="space-between" mt="lg" align="center" style={{borderTop: '1px solid #eee', paddingTop: 20}}>
                <Title order={3}>Total: ₱{formatMoney(calculateTotal())}</Title>
                <Group>
                    <Select 
                        withAsterisk
                        placeholder="Method"
                        value={cashierPaymentMethod}
                        onChange={setCashierPaymentMethod}
                        data={[{ value: 'cash', label: 'Cash' }, { value: 'gcash', label: 'GCash' }, { value: 'bank_transfer', label: 'Bank Transfer' }]}
                        style={{ width: 140 }}
                    />
                    <NumberInput withAsterisk placeholder="Amount Received" value={cashierAmountPaid} onChange={setCashierAmountPaid} decimalScale={2} fixedDecimalScale min={0} leftSection="₱" style={{width: 150}} />
                    <Button color="green" onClick={handleSubmitInvoice} leftSection={<IconCheck size={18} />}>Generate Receipt</Button>
                </Group>
            </Group>
        </Box>
      </Modal>
      
       {/* --- ADMIN PAYMENT / APPROVAL MODAL --- */}
       <Modal 
          opened={openPaymentDialog} 
          onClose={() => setOpenPaymentDialog(false)} 
          title={selectedInvoice?.status === 'pending_approval' ? "Review Online Payment" : "Record Manual Payment"} 
          size="lg"
       >
          <Stack>
            {/* PROOF AREA FOR ONLINE PAYMENTS (SCROLLABLE FOR MOBILE) */}
            {selectedInvoice?.status === 'pending_approval' && verificationData?.receiptImages?.length > 0 && (
                <Card withBorder p="sm" bg="gray.0">
                    <Text size="sm" fw={500} mb="xs" leftSection={<IconPhoto size={16}/>}>Proof of Payment Submitted:</Text>
                    {/* SCROLLABLE CONTAINER */}
                    <ScrollArea w="100%" scrollbars="x">
                        <Group wrap="nowrap" gap="sm"> 
                            {verificationData.receiptImages.map((img, index) => (
                                <Image 
                                    key={index} 
                                    src={img} 
                                    w={80} 
                                    h={80} 
                                    radius="md" 
                                    style={{ cursor: 'pointer', border: '1px solid #ddd', flexShrink: 0 }}
                                    onClick={() => setImagePreview(img)}
                                />
                            ))}
                        </Group>
                    </ScrollArea>
                </Card>
            )}

            <NumberInput 
                label="Amount Paid" 
                withAsterisk
                value={selectedInvoice?.status === 'pending_approval' ? verifyAmount : paymentForm.amountPaid} 
                onChange={(val) => selectedInvoice?.status === 'pending_approval' ? setVerifyAmount(val) : setPaymentForm({ ...paymentForm, amountPaid: val })} 
                decimalScale={2} 
                fixedDecimalScale 
                max={100000}
                leftSection={<Text size="xs">₱</Text>} 
            />
            
            <Select 
                label="Payment Method" 
                withAsterisk
                value={paymentForm.paymentMethod} 
                onChange={(val) => setPaymentForm({ ...paymentForm, paymentMethod: val })} 
                data={[{ value: 'cash', label: 'Cash' }, { value: 'bank_transfer', label: 'Bank Transfer' }, { value: 'gcash', label: 'GCash' }]} 
            />
            
            {/* CONDITIONAL REFERENCE NO INPUT: Hidden if payment is CASH */}
            {paymentForm.paymentMethod !== 'cash' && (
                <TextInput 
                    label="Reference No." 
                    placeholder="XXXX XXX XXXXXX"
                    description={paymentForm.paymentMethod === 'gcash' ? "GCash Ref No. must be 13 digits" : "Transaction ID or Receipt No."}
                    withAsterisk
                    value={paymentForm.referenceNumber} 
                    onChange={(e) => handleRefInput(e.target.value)} 
                    maxLength={paymentForm.paymentMethod === 'gcash' ? 15 : 50}
                />
            )}

            {/* REJECTION REASON INPUT (Only for Online Approvals) */}
            {selectedInvoice?.status === 'pending_approval' && (
                <Textarea 
                    label="Rejection Reason" 
                    placeholder="If rejecting, please state why (e.g., Invalid amount, unclear receipt)"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                />
            )}
          </Stack>

          <Group justify="flex-end" mt="xl">
            <Button variant="default" onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
            
            {selectedInvoice?.status === 'pending_approval' ? (
                <>
                    <Button color="red" variant="light" onClick={() => handleAdminAction('rejected')}>Reject</Button>
                    <Button color="green" onClick={() => handleAdminAction('approve')}>Approve & Verify</Button>
                </>
            ) : (
                <Button color="green" onClick={() => handleAdminAction('manual')}>Confirm Payment</Button>
            )}
          </Group>
      </Modal>

      <Modal opened={openPrintDialog} onClose={() => setOpenPrintDialog(false)} title="View Receipt" size="lg">
        {/* Scrollable wrapper for mobile viewing */}
        <div style={{ padding: '20px', border: '1px solid #ddd', overflowX: 'auto' }}>
            <div style={{ minWidth: '500px' }}>
                {selectedInvoice && <InvoiceTemplate invoice={selectedInvoice} />}
            </div>
        </div>
        <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setOpenPrintDialog(false)}>Close</Button>
            <Button 
                leftSection={printing ? <Loader size={16} color="white" /> : <IconPrinter size={18} />} 
                onClick={handlePrintReceipt}
                disabled={printing}
            >
                {printing ? 'Preparing...' : 'Print Receipt'}
            </Button>
        </Group>
      </Modal>
      
      <Modal opened={openHistoryDialog} onClose={() => setOpenHistoryDialog(false)} title="Payment History" size="lg">
         <Text size="sm" mb="xs">Invoice: <strong>{selectedInvoice?.description}</strong></Text>
         
         <Group grow mb="md" mt="xs">
            <Paper withBorder p="xs">
                <Text size="xs" c="dimmed">Total Amount</Text>
                <Text fw={700}>₱{formatMoney(selectedInvoice?.totalAmount)}</Text>
            </Paper>
            <Paper withBorder p="xs">
                <Text size="xs" c="dimmed">Total Paid</Text>
                <Text fw={700} c="green">₱{formatMoney(selectedInvoice?.amountPaid || 0)}</Text>
            </Paper>
            <Paper withBorder p="xs">
                <Text size="xs" c="dimmed">Balance</Text>
                <Text fw={700} c="red">₱{formatMoney((selectedInvoice?.totalAmount || 0) - (selectedInvoice?.amountPaid || 0))}</Text>
            </Paper>
         </Group>

        <Paper withBorder>
          <ScrollArea>
            <Table striped style={{ minWidth: '500px' }}>
                <Table.Thead><Table.Tr><Table.Th>Date</Table.Th><Table.Th>Amount</Table.Th><Table.Th>Method</Table.Th><Table.Th>Status</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>{historyData.length === 0 ? (<Table.Tr><Table.Td colSpan={4} align="center">No transactions found</Table.Td></Table.Tr>) : (historyData.map((tx) => (<Table.Tr key={tx._id}><Table.Td>{new Date(tx.paymentDate).toLocaleDateString()}</Table.Td><Table.Td>{formatMoney(tx.amountPaid)}</Table.Td><Table.Td style={{ textTransform: 'capitalize' }}>{tx.paymentMethod}</Table.Td><Table.Td><Badge color={tx.status === 'completed' ? 'green' : 'red'}>{tx.status}</Badge></Table.Td></Table.Tr>)))}</Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Modal>

       <Modal opened={!!imagePreview} onClose={() => setImagePreview(null)} size="xl" withCloseButton={false} styles={{ body: { padding: 0 }, content: { backgroundColor: 'transparent', boxShadow: 'none' } }} centered>
        <Box pos="relative"><ActionIcon onClick={() => setImagePreview(null)} color="gray" variant="filled" radius="xl" style={{ position: 'absolute', right: 10, top: 10, zIndex: 10 }}><IconX size={20} /></ActionIcon><Image src={imagePreview} radius="md" /></Box>
      </Modal>
    </Layout>
  );
};

export default Billing;